import { useRef, useState } from 'react'

import { DEFAULT_PAGE_SIZE } from '../constants/listing'

export type InfinitePage<T> = {
  content: T[]
  page: number
  last: boolean
}

type UseInfiniteLoadOptions<T> = {
  pageSize?: number
  getItemKey: (item: T) => string | number
  fetchPage: (
    page: number,
    pageSize: number
  ) => Promise<InfinitePage<T> | null>
}

const dedupeByKey = <T,>(
  items: T[],
  getItemKey: (item: T) => string | number
) => {
  const seen = new Set<string | number>()

  return items.filter(item => {
    const key = getItemKey(item)

    if (seen.has(key)) {
      return false
    }

    seen.add(key)
    return true
  })
}

export function useInfiniteLoad<T>({
  pageSize = DEFAULT_PAGE_SIZE,
  getItemKey,
  fetchPage,
}: UseInfiniteLoadOptions<T>) {
  const [items, setItems] = useState<T[] | null>(null)
  const [page, setPage] = useState(0)
  const [lastPage, setLastPage] = useState(false)
  const [loading, setLoading] = useState(false)
  const loadingMoreRef = useRef(false)

  const reset = () => {
    setItems(null)
    setPage(0)
    setLastPage(false)
  }

  const applyPage = (
    response: InfinitePage<T>,
    options?: {
      prepend?: T[]
      replace?: boolean
    }
  ) => {
    const prepend = options?.prepend ?? []
    const replace = options?.replace ?? false

    setItems(current =>
      dedupeByKey(
        replace
          ? [...prepend, ...response.content]
          : [...(current ?? []), ...response.content],
        getItemKey
      )
    )
    setPage(response.page)
    setLastPage(response.last)
  }

  const prependItems = (seed: T[]) => {
    if (seed.length === 0) {
      return
    }

    setItems(current =>
      dedupeByKey([...seed, ...(current ?? [])], getItemKey)
    )
  }

  const loadInitial = async (prepend: T[] = []) => {
    setLoading(true)

    try {
      const response = await fetchPage(0, pageSize)

      if (!response) {
        return false
      }

      applyPage(response, { prepend, replace: true })
      return true
    } finally {
      setLoading(false)
    }
  }

  const loadMore = async () => {
    if (loadingMoreRef.current || loading || lastPage || items === null) {
      return
    }

    loadingMoreRef.current = true
    setLoading(true)

    try {
      const response = await fetchPage(page + 1, pageSize)

      if (!response) {
        return
      }

      applyPage(response)
    } finally {
      loadingMoreRef.current = false
      setLoading(false)
    }
  }

  return {
    items,
    loading,
    lastPage,
    page,
    pageSize,
    loadInitial,
    loadMore,
    applyPage,
    prependItems,
    reset,
    setItems,
  }
}
